/**
 * Submenu flutuante do modo colapsado (JOR4-7365, RN04).
 *
 * Vai para um portal no body, com `position: fixed`, e não como filho absoluto
 * do ícone. Dois motivos:
 *
 * 1. O dxDrawer recorta o painel na largura colapsada — um filho posicionado
 *    dentro dele seria cortado no primeiro pixel além dos 56px;
 * 2. é o que a Observação Técnica do card pede, para o painel não empurrar o
 *    conteúdo da tela ao aparecer.
 */

import { useEffect, useRef, type CSSProperties } from "react";
import { createPortal } from "react-dom";

import { contemClasseAtiva, type No } from "./arvoreMenu";
import { TAMANHO_ICONE_ITEM, iconeDoMenu } from "./iconesMenu";
import type { ItemMenu } from "./montarMenu";

export interface PropsFlyoutSubmenu {
    no: No;
    /** Retângulo do ícone que abriu o painel, em coordenadas de viewport. */
    ancora: DOMRect;
    classeAtiva: string | null;
    aoAbrirTela: (item: ItemMenu) => void;
    /** Entrar no painel cancela o fechamento agendado pela saída do ícone. */
    aoEntrar: () => void;
    aoSair: () => void;
    /** Fecha e devolve o foco ao ícone — Escape, ou sair do último item. */
    aoFechar: () => void;
    /**
     * Aberto por teclado: o foco precisa entrar no painel. O portal fica no fim
     * do body, então o Tab natural nunca chegaria aqui.
     */
    focarPrimeiroItem: boolean;
}

/** Folga entre o ícone e o painel, e do painel para a borda da janela. */
const AFASTAMENTO = 4;
const MARGEM_JANELA = 8;

/**
 * Telas que o painel lista. Em produção o menu tem dois níveis e isso é
 * simplesmente `no.filhos`, mas o cadastro permite mais — e um terceiro nível
 * ficaria inalcançável no modo colapsado, já que o painel não abre outro painel.
 * Então desce a árvore e traz tudo que abre tela.
 */
export const telasDoPainel = (no: No): No[] => {
    const encontradas: No[] = [];

    const visitar = (atual: No) => {
        for (const filho of atual.filhos) {
            // Folha, ou grupo que também abre tela.
            if (filho.filhos.length === 0 || filho.item.classInvoke) {
                encontradas.push(filho);
            }
            if (filho.filhos.length > 0) visitar(filho);
        }
    };

    visitar(no);
    return encontradas;
};

export const FlyoutSubmenu = ({
    no,
    ancora,
    classeAtiva,
    aoAbrirTela,
    aoEntrar,
    aoSair,
    aoFechar,
    focarPrimeiroItem,
}: PropsFlyoutSubmenu) => {
    const painel = useRef<HTMLDivElement>(null);

    const itens = () =>
        Array.from(
            painel.current?.querySelectorAll<HTMLButtonElement>(
                ".menu-ds-flyout-lista button",
            ) ?? [],
        );

    /** Move o foco na lista. `absoluto` ignora a posição atual. */
    const mover = (delta: number, absoluto?: number) => {
        const botoes = itens();
        if (!botoes.length) return;

        const atual = botoes.indexOf(
            document.activeElement as HTMLButtonElement,
        );
        const destino =
            absoluto ?? (atual < 0 ? 0 : atual + delta);

        botoes[Math.max(0, Math.min(destino, botoes.length - 1))]?.focus();
    };

    useEffect(() => {
        if (!focarPrimeiroItem) return;
        itens()[0]?.focus();
        // Só na abertura por teclado; `itens` lê o DOM e não é dependência.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focarPrimeiroItem]);

    /*
     * A altura só se conhece depois de renderizar, então em vez de reposicionar
     * o painel para caber, ele é limitado pelo que sobra da janela e rola por
     * dentro. Um grupo com muitas telas perto do rodapé continua acessível.
     */
    const estilo: CSSProperties = {
        left: ancora.right + AFASTAMENTO,
        top: ancora.top,
        maxHeight: `calc(100vh - ${ancora.top + MARGEM_JANELA}px)`,
    };

    return createPortal(
        <div
            ref={painel}
            className="menu-ds-flyout"
            style={estilo}
            role="menu"
            aria-label={no.item.text}
            onMouseEnter={aoEntrar}
            onMouseLeave={aoSair}
            // onFocus e onBlur borbulham em React: manter o painel aberto
            // enquanto o foco estiver dentro dele é o que torna o teclado viável.
            onFocus={aoEntrar}
            onBlur={aoSair}
            onKeyDown={(evento) => {
                switch (evento.key) {
                    // ← é o par do → que abriu o painel. Sem ele, o único
                    // caminho de volta era o Escape, que ninguém adivinha.
                    case "ArrowLeft":
                    case "Escape":
                        // Não deixa o Escape chegar ao ícone e ser tratado duas
                        // vezes.
                        evento.stopPropagation();
                        aoFechar();
                        break;
                    case "ArrowDown":
                        mover(1);
                        break;
                    case "ArrowUp":
                        mover(-1);
                        break;
                    case "Home":
                        mover(0, 0);
                        break;
                    case "End":
                        mover(0, itens().length - 1);
                        break;
                    default:
                        // Enter e Espaço seguem o <button> nativo.
                        return;
                }

                evento.preventDefault();
            }}
        >
            <div className="menu-ds-flyout-titulo">{no.item.text}</div>

            <div className="menu-ds-flyout-lista">
                {telasDoPainel(no).map((filho) => {
                    const ativo = contemClasseAtiva(filho, classeAtiva);
                    const Icone = iconeDoMenu(filho.item);
                    return (
                        <button
                            key={filho.item.ID}
                            type="button"
                            className={`menu-ds-flyout-item${ativo ? " is-ativo" : ""}`}
                            role="menuitem"
                            onClick={() => {
                                aoAbrirTela(filho.item);
                                // Sem isto o painel só some quando o mouse sai.
                                aoFechar();
                            }}
                            title={filho.item.text}
                        >
                            <span className="menu-ds-item-icone">
                                <Icone size={TAMANHO_ICONE_ITEM} />
                            </span>
                            <span className="menu-ds-rotulo">
                                {filho.item.text}
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>,
        document.body,
    );
};
